import { useState, useRef, useEffect } from 'react';
import { COOKING_TERMS, TERM_PATTERN } from '../data/cookingTerms';

function TermPopover({ term, position, onClose }) {
  const data = COOKING_TERMS[term.toLowerCase()];
  if (!data) return null;

  const speak = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(`${data.term}. ${data.definition}`);
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <div
      className="fixed z-[70] animate-fadeIn"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, 8px)',
      }}
    >
      {/* Arrow */}
      <div className="absolute left-1/2 -top-2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-800 rotate-45 border-l border-t border-gray-200 dark:border-gray-700" />

      {/* Popover content */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-72 max-w-[90vw]">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="text-lg">{data.icon}</span>
            <h4 className="font-semibold text-gray-900 dark:text-white">{data.term}</h4>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Definition */}
        <div className="p-3">
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {data.definition}
          </p>

          {/* Speak button */}
          <button
            onClick={speak}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-3 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
            Speak Definition
          </button>
        </div>
      </div>
    </div>
  );
}

// Highlighted text component that shows popovers
export function HighlightedText({ text, darkMode = false }) {
  const [activeTerm, setActiveTerm] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setActiveTerm(null);
      }
    };

    if (activeTerm) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeTerm]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setActiveTerm(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleTermClick = (e, term) => {
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    setPopoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom,
    });
    setActiveTerm(activeTerm === term ? null : term);
  };

  // Split text into parts with highlighted terms
  const parts = [];
  let lastIndex = 0;
  let match;

  const pattern = new RegExp(TERM_PATTERN.source, 'gi');

  while ((match = pattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add the matched term
    parts.push({
      type: 'term',
      content: match[0],
      key: match[0].toLowerCase(),
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  const termClass = darkMode
    ? 'text-primary-400 underline decoration-dotted cursor-pointer hover:text-primary-300'
    : 'text-primary-600 underline decoration-dotted cursor-pointer hover:text-primary-500';

  return (
    <span ref={containerRef}>
      {parts.map((part, index) =>
        part.type === 'term' ? (
          <span
            key={index}
            onClick={(e) => handleTermClick(e, part.key)}
            className={termClass}
            title={`Click to learn about "${part.content}"`}
          >
            {part.content}
          </span>
        ) : (
          <span key={index}>{part.content}</span>
        )
      )}

      {activeTerm && (
        <TermPopover
          term={activeTerm}
          position={popoverPosition}
          onClose={() => setActiveTerm(null)}
        />
      )}
    </span>
  );
}

export default HighlightedText;
